/**
 * rPI OmniDesk — CustomerThreads API client (E-OMNI, F-1a read-wire)
 *
 * Wraps the OmniDesk controller (`@Controller('omni/threads')`):
 *  - GET    /api/v1/omni/threads         — list + filters + cursor pagination
 *  - GET    /api/v1/omni/threads/:id     — thread detail + nested messages
 *  - POST   /api/v1/omni/threads/:id/reply — outbound reply (TRIZ optimistic)
 *  - PATCH  /api/v1/omni/threads/:id     — status/assignee/partner mutation
 *
 * The GET-flow (F-1a) is WireSchema-shaped: the backend maps Prisma → the
 * `@kgc/api-contract` OmniDesk DTOs (`ThreadSummary` / `ThreadDetail`). The FE
 * `safeParse`-gates every read against the pinned schema (JF-4 wire-gate,
 * #719 pattern) — a shape drift throws a typed error the hook layer surfaces,
 * instead of leaking an unvalidated payload into the UI.
 *
 * The write-flow (reply/PATCH) keeps its legacy response shape for now — the
 * write re-shape (reply→OmniMessage, patch→ThreadSummary) lands with F-1b.
 * Only the endpoint prefix moved to `/omni`.
 */

import {
  OmniMessageSchema,
  ThreadDetailSchema,
  ThreadListPayloadSchema,
  type MessageSyncStatus,
  type ThreadChannel,
  type ThreadDetail,
  type ThreadStatus,
  type ThreadSummary,
} from '@kgc/api-contract';

import { api } from './client';

// Re-export the wire types so page/hook consumers import from one place.
export type { MessageDirection, MessageSyncStatus, OmniMessage } from '@kgc/api-contract';
export type { ThreadChannel, ThreadDetail, ThreadStatus, ThreadSummary };

// Aliases kept for callers that imported the original names.
export type ApiThreadChannel = ThreadChannel;
export type ApiThreadStatus = ThreadStatus;

// ============================================
// FILTERS
// ============================================

export interface ThreadListFilters {
  status?: ThreadStatus | undefined;
  channel?: ThreadChannel | undefined;
  partnerId?: string | undefined;
  assigneeUserId?: string | undefined;
  /**
   * F-2 RBAC nézet-váltó — `unassigned=true` → the BE filters `assigneeUserId IS NULL`
   * (the "Kiosztatlan" view). Mutually exclusive with `assigneeUserId`: the BE DTO
   * rejects both with 400, and the InboxList view-switcher enforces the exclusivity
   * FE-side (a single tri-state selects ME → `assigneeUserId`, UNASSIGNED → `unassigned`).
   */
  unassigned?: boolean | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
}

/** @deprecated Use `ThreadListFilters` — pagination merged into the single filter object. */
export type ThreadListPagination = Pick<ThreadListFilters, 'cursor' | 'limit'>;

export interface ThreadListResponse {
  data: ThreadSummary[];
  nextCursor?: string;
}

// ============================================
// WRITE bodies / responses (legacy shape — F-1b re-shapes the response)
// ============================================

export interface ReplyBody {
  content: string;
  contentHtml?: string;
}

export interface ReplyResponse {
  messageId: string;
  syncStatus: MessageSyncStatus;
}

/**
 * Normalized reply result — a **transition-guard** across the F-1b write re-shape.
 * Until F-1b L1 re-shapes the reply response to `{ data: OmniMessage }`, the BE
 * echoes the legacy `{ messageId, syncStatus }` shape. `replyToThread` normalizes
 * BOTH into `{ id, syncStatus }` so the page-layer optimistic reconciliation is
 * merge-order-independent — it never reads `messageId`, which disappears the moment
 * F-1b lands. When F-1b L1 pins the exact wire shape, a follow-up PR tightens the
 * legacy branch out and can surface the full `OmniMessage` (id/author/attachments).
 */
export interface ReplyResult {
  id: string;
  syncStatus: MessageSyncStatus;
}

export interface PatchThreadBody {
  status?: ThreadStatus;
  assigneeUserId?: string | null;
  partnerId?: string | null;
}

// ============================================
// WIRE-GATE
// ============================================

/**
 * Validate an OmniDesk read payload against its pinned WireSchema. A parse
 * failure means the BE↔FE contract drifted — throw a typed error the hook
 * error-branch renders, rather than passing an unvalidated shape downstream.
 */
function parseWire<T>(
  schema: { safeParse: (v: unknown) => { success: true; data: T } | { success: false } },
  payload: unknown,
  context: string
): T {
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(`OmniDesk wire-gate: ${context} response did not match the pinned contract`);
  }
  return parsed.data;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * List OmniDesk customer threads (inbox). Tenant-scoped server-side
 * (`where: { tenantId }` from `req.user.tenantId`). Supports `partnerId`
 * filter for the Partner Timeline and cursor pagination.
 */
export async function listThreads(
  filters: ThreadListFilters = {},
  pagination: ThreadListPagination = {}
): Promise<ThreadListResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.channel) params.append('channel', filters.channel);
  if (filters.partnerId) params.append('partnerId', filters.partnerId);
  if (filters.assigneeUserId) params.append('assigneeUserId', filters.assigneeUserId);
  // F-2: the BE DTO is `z.enum(['true','false'])` — send the literal string, never a bool.
  // Mutually exclusive with assigneeUserId (the view-switcher never sets both).
  if (filters.unassigned) params.append('unassigned', 'true');
  const cursor = pagination.cursor ?? filters.cursor;
  if (cursor) params.append('cursor', cursor);
  const limit = pagination.limit ?? filters.limit;
  if (limit !== undefined) params.append('limit', limit.toString());

  const qs = params.toString();
  const endpoint = qs ? `/omni/threads?${qs}` : '/omni/threads';

  const raw = await api.get<unknown>(endpoint);
  const payload = parseWire(ThreadListPayloadSchema, raw, 'list');
  return { data: payload.data, ...(payload.nextCursor ? { nextCursor: payload.nextCursor } : {}) };
}

/**
 * GET /api/v1/omni/threads/:id — a single thread + its messages.
 * Backend returns 404 on cross-tenant probe (ADR-106 tenant-scope guard).
 */
export async function getThread(id: string): Promise<{ data: ThreadDetail }> {
  const raw = await api.get<{ data: unknown }>(`/omni/threads/${id}`);
  return { data: parseWire(ThreadDetailSchema, raw?.data, 'detail') };
}

/**
 * Legacy reply-response type-guard — matches the pre-F-1b `{ messageId, syncStatus }`
 * echo. Kept until F-1b L1 re-shapes the response to the `OmniMessage` wire shape.
 */
function isLegacyReplyResponse(value: unknown): value is ReplyResponse {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.messageId === 'string' && typeof v.syncStatus === 'string';
}

/**
 * POST /api/v1/omni/threads/:id/reply — outbound reply (TRIZ optimistic).
 *
 * Transition-guard for the F-1b write re-shape: the BE currently echoes the legacy
 * `{ messageId, syncStatus }` shape and re-shapes to `{ data: OmniMessage }` with
 * F-1b L1. This normalizes both into `ReplyResult` so the page-layer reconciliation
 * is merge-order-independent (never reads `messageId`). A payload matching neither
 * shape throws a typed wire-gate error (fail-loud) instead of silently reconciling
 * the optimistic row to `id: undefined`.
 */
export async function replyToThread(id: string, body: ReplyBody): Promise<{ data: ReplyResult }> {
  const raw = await api.post<{ data: unknown }>(`/omni/threads/${id}/reply`, body);
  const payload = raw?.data;

  const wire = OmniMessageSchema.safeParse(payload);
  if (wire.success) {
    return { data: { id: wire.data.id, syncStatus: wire.data.syncStatus } };
  }

  if (isLegacyReplyResponse(payload)) {
    return { data: { id: payload.messageId, syncStatus: payload.syncStatus } };
  }

  throw new Error(
    'OmniDesk wire-gate: reply response matched neither the OmniMessage contract nor the legacy { messageId, syncStatus } shape'
  );
}

/**
 * PATCH /api/v1/omni/threads/:id — mutate status / assignee / partner link.
 * At least one field is required (backend zod refine). Response shape is legacy
 * until F-1b re-shapes it to `{ data: ThreadSummary }`.
 */
export async function patchThread(
  id: string,
  body: PatchThreadBody
): Promise<{ data: ThreadSummary }> {
  return api.patch<{ data: ThreadSummary }>(`/omni/threads/${id}`, body);
}
